<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import LanguagePicker from './LanguagePicker.vue'

const props = defineProps<{
  activeTab: string
}>()

const emit = defineEmits<{
  'navigate': [tab: string]
}>()

const { t } = useI18n()
const showLangPicker = ref(false)

const isActive = (tab: string) => props.activeTab === tab

const go = (tab: string) => emit('navigate', tab)

// Publish the rendered header height as `--app-header-height` so other
// fixed-position surfaces (the chat dock) can size themselves to fill
// the space below the header. Covers orientation changes and
// safe-area-inset-top shifts via ResizeObserver.
const headerEl = ref<HTMLElement | null>(null)
let headerObserver: ResizeObserver | null = null
onMounted(() => {
  // Deep-link from the static /articles header: /?openLang=1 pops the
  // language picker straight away so tapping the globe on an article
  // feels like tapping the globe in the app. Strip the param so a
  // reload doesn't re-open it.
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('openLang') === '1') {
      showLangPicker.value = true
      url.searchParams.delete('openLang')
      window.history.replaceState({}, '', url.toString())
    }
  } catch {}

  if (!headerEl.value || typeof ResizeObserver === 'undefined') return
  const apply = () => {
    const h = headerEl.value?.offsetHeight
    if (h) document.documentElement.style.setProperty('--app-header-height', `${h}px`)
  }
  headerObserver = new ResizeObserver(apply)
  headerObserver.observe(headerEl.value)
  apply()
})
onBeforeUnmount(() => {
  headerObserver?.disconnect()
  headerObserver = null
})
</script>

<template>
  <header ref="headerEl" class="app-header">
    <button
      type="button"
      class="app-header__brand"
      :aria-label="t('common.home')"
      @click="go('ForecastScreen')"
    >
      <img
        src="/icons/logo_192x192.png"
        class="app-header__icon"
        alt=""
        aria-hidden="true"
      />
      <span class="app-header__wordmark">WITAL</span>
    </button>

    <nav class="app-header__nav" :aria-label="t('navigation.primary')">
      <button
        type="button"
        class="app-header__icon-btn"
        :class="{ 'app-header__icon-btn--active': isActive('AboutScreen') }"
        :aria-label="t('navigation.about')"
        :aria-pressed="isActive('AboutScreen')"
        @click="go('AboutScreen')"
      >
        <v-icon icon="mdi-information-outline" size="22" />
      </button>

      <button
        type="button"
        class="app-header__icon-btn"
        :class="{ 'app-header__icon-btn--active': isActive('LocationScreen') }"
        :aria-label="t('navigation.location')"
        :aria-pressed="isActive('LocationScreen')"
        @click="go('LocationScreen')"
      >
        <v-icon icon="mdi-map-marker-outline" size="22" />
      </button>

      <button
        type="button"
        class="app-header__icon-btn"
        :class="{ 'app-header__icon-btn--active': isActive('SettingsScreen') }"
        :aria-label="t('navigation.settings')"
        :aria-pressed="isActive('SettingsScreen')"
        @click="go('SettingsScreen')"
      >
        <v-icon icon="mdi-cog-outline" size="22" />
      </button>

      <!-- Charts. Occupies the slot /articles used to hold rather
           than adding a sixth icon: the nav row is already at the
           density its 420px rules were sized for. /articles stays
           reachable by URL, it just loses this entry point. -->
      <button
        type="button"
        class="app-header__icon-btn"
        :class="{ 'app-header__icon-btn--active': isActive('ChartsScreen') }"
        :aria-label="t('navigation.charts')"
        :aria-pressed="isActive('ChartsScreen')"
        @click="go('ChartsScreen')"
      >
        <v-icon icon="mdi-chart-box-outline" size="22" />
      </button>

      <button
        type="button"
        class="app-header__icon-btn app-header__icon-btn--globe"
        :class="{ 'app-header__icon-btn--active': showLangPicker }"
        :aria-label="t('navigation.language')"
        :aria-pressed="showLangPicker"
        @click="showLangPicker = true"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      </button>
    </nav>

    <LanguagePicker v-model="showLangPicker" />
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  padding-top: calc(8px + var(--safe-area-top));
  padding-left: calc(12px + env(safe-area-inset-left));
  padding-right: calc(12px + env(safe-area-inset-right));
  /* Slight tint over the page surface, then a very soft shadow for
     depth. Replaces the old hard border-bottom — reads as a distinct
     zone without the harsh line. color-mix works in all modern
     browsers (Safari 16.2+, Chrome 111+, Firefox 113+). */
  background: color-mix(in srgb, rgb(var(--v-theme-on-surface)) 5%, rgb(var(--v-theme-surface)));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 10;
}

.app-header__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  background: transparent;
  border: 0;
  cursor: pointer;
  border-radius: 8px;
  min-height: 44px;
}

.app-header__brand:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.app-header__icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  flex-shrink: 0;
}

.app-header__wordmark {
  /* High-contrast solid colour beats a gradient for small-size legibility
     and also renders correctly under both light and dark themes. */
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.03em;
  color: rgb(var(--v-theme-primary));
}

.app-header__nav {
  display: flex;
  align-items: center;
  /* Generous gap so adjacent tap-zones don't touch — the 44×44 button
   * size meets the target, but butting them up against each other
   * still invites fat-finger misfires at the seam. */
  gap: 8px;
}

.app-header__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 8px;
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: rgba(var(--v-theme-on-surface), 0.78);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.app-header__icon-btn:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: rgb(var(--v-theme-on-surface));
}

.app-header__icon-btn:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.app-header__icon-btn--active {
  background: rgba(var(--v-theme-on-surface), 0.1);
  color: rgb(var(--v-theme-on-surface));
}

.app-header__icon-btn--link,
.app-header__icon-btn--link:hover {
  text-decoration: none;
}

/* Tighten the header once we add a 5th nav icon. On narrow viewports
   the logo goes first — "WITAL" is the stronger identity cue than the
   icon and reads instantly at small sizes where the icon becomes a
   tiny abstract mark. */
@media (max-width: 420px) {
  .app-header__icon {
    display: none;
  }
  .app-header {
    padding-left: calc(8px + env(safe-area-inset-left));
    padding-right: calc(8px + env(safe-area-inset-right));
  }
  .app-header__icon-btn {
    min-width: 40px;
    padding: 6px;
  }
  .app-header__nav {
    gap: 4px;
  }
}
</style>
